import AutoSizer from 'react-virtualized-auto-sizer';
import CollectionsIcon from '@mui/icons-material/PlaylistAdd';
import IntervalChooser from '../IntervalChooser.jsx';
import Loading from '../Loading.jsx';
import PropTypes from 'prop-types';
import React, {useMemo} from 'react';
import {Box, Divider, Slide, Switch, Typography, styled} from '@mui/material';
import {FixedSizeList as List} from 'react-window';
import {ObservationItem} from './ObservationItem.jsx';
import {SortPicker} from '../SortPicker.jsx';

const GreyBox = styled(Box)(({theme}) => ({
  display: 'flex',
  alignItems: 'center',
  color: theme.palette.grey[600],
}));

GreyBox.defaultProps = {
  padding: 1,
};

function getSortFunction(sortOrder) {
  if (sortOrder.column === 'date' && !sortOrder.desc) {
    return (a, b) => (Date.parse(a.observed) > Date.parse(b.observed) ? 1 : -1);
  } else if (sortOrder.column === 'count' && sortOrder.desc) {
    return (a, b) => (a.count > b.count ? -1 : 1);
  } else if (sortOrder.column === 'count' && !sortOrder.desc) {
    return (a, b) => (a.count < b.count ? -1 : 1);
  }

  throw new Error(`Unknown sort: ${sortOrder.column}`);
}

const ObservationChooser = ({
  interval,
  observations,
  onObservationClick,
  onObservationZoomClick,
  selected,
  loading,
  onDateRangeChange,
  dateRange,
  minDate,
  maxDate,
  toggleZero,
  showZero,
  seriesInfo,
  sortOrder,
  onSortOrderChange,
}) => {
  const filteredObservations = useMemo(() => {
    const obs = observations.filter(
      observation => showZero || observation.count > 0,
    );
    if (sortOrder.column === 'date' && sortOrder.desc) {
      return obs;
    }
    return obs.sort(getSortFunction(sortOrder));
  }, [observations, sortOrder, showZero]);

  return (
    <Box flexGrow={1} display="flex" flexDirection="column">
      <Box padding={1}>
        <Divider />

        <Box py={1}>
          <IntervalChooser
            interval={interval}
            minDate={minDate}
            maxDate={maxDate}
            range={dateRange}
            handleChange={onDateRangeChange}
          />
        </Box>
        <Divider />

        <GreyBox paddingTop={1} padding={0} display="flex" alignItems="center">
          <Box display="flex" flex={1} alignItems="center">
            <Typography variant="body2">Show zero detects</Typography>
            <Switch
              disabled={loading}
              checked={showZero}
              onChange={toggleZero}
              value="show-zero"
              color="primary"
              inputProps={{'aria-label': 'primary checkbox'}}
            />
          </Box>
          <Box
            display="flex"
            flex={1}
            alignItems="center"
            justifyContent="right"
          >
            <CollectionsIcon />
            <SortPicker
              value={sortOrder}
              onChange={nextSortOrder => onSortOrderChange(nextSortOrder)}
            />
          </Box>
        </GreyBox>
      </Box>
      <Divider />
      <Box
        position="relative"
        flexGrow={1}
        display="flex"
        flexDirection="column"
      >
        <Slide direction="right" in={!!observations} unmountOnExit mountOnEnter>
          <Box flex={1} flexGrow={1} bgcolor="white">
            <AutoSizer>
              {({height, width}) => (
                <List
                  itemData={loading ? [] : filteredObservations}
                  width={width}
                  height={height}
                  itemCount={loading ? 0 : filteredObservations.length}
                  itemSize={80}
                  useIsScrolling
                >
                  {({data, index, style}) => {
                    const item = data[index];
                    const nextItem = data[index + 1];

                    return (
                      <ObservationItem
                        style={style}
                        item={item}
                        nextItem={nextItem}
                        onClick={() => onObservationClick(item)}
                        onZoomClick={() => onObservationZoomClick(item)}
                        selected={selected === item.layer}
                        seriesInfo={seriesInfo}
                      />
                    );
                  }}
                </List>
              )}
            </AutoSizer>
          </Box>
        </Slide>
        <Loading
          cover
          coverColor="white"
          loading={loading}
          text="Loading Imagery"
        />
      </Box>
    </Box>
  );
};

ObservationChooser.propTypes = {
  interval: PropTypes.oneOf(['month', 'year']),
  onObservationClick: PropTypes.func.isRequired,
  onObservationZoomClick: PropTypes.func.isRequired,
  observations: PropTypes.array,
  selected: PropTypes.string,
  loading: PropTypes.bool,
  onDateRangeChange: PropTypes.func.isRequired,
  dateRange: PropTypes.arrayOf(PropTypes.instanceOf(Date)).isRequired,
  minDate: PropTypes.instanceOf(Date).isRequired,
  maxDate: PropTypes.instanceOf(Date),
  toggleZero: PropTypes.func.isRequired,
  showZero: PropTypes.bool,
  seriesInfo: PropTypes.object,
  onSortOrderChange: PropTypes.func,
  sortOrder: PropTypes.object,
};

export default ObservationChooser;
